import { Component, OnInit, OnChanges, SimpleChanges } from '@angular/core';
import { CommonModule } from '@angular/common';
import { Router } from '@angular/router';
import { MatTooltipModule } from '@angular/material/tooltip';
import { MatButtonModule } from '@angular/material/button';
import { HighchartsChartModule } from 'highcharts-angular';
import * as Highcharts from 'highcharts';
import { ChartData, formatDashboardUserEmail } from '../../services/user-dashboard.service';
import { NotificationService } from '../../services/notification.service';
import { SENTIMENT_SANKEY_COLORS } from '../../shared/constant';
import { LargeChartViewService } from '../../services/large-chart-view.service';
import { formatDateTimeInTimezone, formatUserFriendlyDateTimeInTimezone } from '../../shared/chart-timezone.util';
import {
  normalizeCategoryArray,
  normalizeChartDisplayLabel,
  normalizeHighchartsApiConfig
} from '../../shared/chart-label.util';
import {
  collectTableColumnKeys,
  downloadChartTableCsv,
  extractChartTableRows
} from '../../shared/chart-table-csv-export.util';
import {
  buildInsightsConfirmMessage,
  chartTableVolumeLabel,
  describeRowDrilldown,
  InsightsConfirmSelection,
  mergePeriodSlices,
  readChartQueryContext,
  RowDrilldown
} from '../../shared/chart-insights-drilldown.util';
import { ChartInsightsDrilldownService } from '../../services/chart-insights-drilldown.service';
import { ConfirmPopupService } from '../../services/confirm-popup.service';

// Import Highcharts modules for special chart types
import HighchartsSankey from 'highcharts/modules/sankey';
import HighchartsHeatmap from 'highcharts/modules/heatmap';
import HighchartsTreemap from 'highcharts/modules/treemap';
import HighchartsExporting from 'highcharts/modules/exporting';
import HighchartsOfflineExporting from 'highcharts/modules/offline-exporting';
import HighchartsExportData from 'highcharts/modules/export-data';
import HighchartsMore from 'highcharts/highcharts-more';
import HighchartsSolidGauge from 'highcharts/modules/solid-gauge';
import HighchartsFunnel from 'highcharts/modules/funnel';

// Initialize modules
HighchartsSankey(Highcharts);
HighchartsHeatmap(Highcharts);
HighchartsTreemap(Highcharts);
HighchartsExporting(Highcharts);
HighchartsOfflineExporting(Highcharts);
HighchartsExportData(Highcharts);
HighchartsMore(Highcharts);
HighchartsSolidGauge(Highcharts);
HighchartsFunnel(Highcharts);

// Set global Highcharts options to disable thousand separators
Highcharts.setOptions({
  lang: {
    thousandsSep: ''
  },
  tooltip: {
    pointFormat: '{point.name}: {point.y}'
  }
});

@Component({
  selector: 'app-large-chart-view',
  standalone: true,
  imports: [
    CommonModule,
    MatTooltipModule,
    MatButtonModule,
    HighchartsChartModule
  ],
  templateUrl: './large-chart-view.component.html',
  styleUrl: './large-chart-view.component.scss'
})
export class LargeChartViewComponent implements OnInit, OnChanges {
  Highcharts: typeof Highcharts = Highcharts;
  chartOptions: Highcharts.Options = {};
  chartRef: any;
  isLoading = false;
  hasError = false;
  errorMessage = '';
  chartData: ChartData | null = null;
  insightsBusy = false;
  private selectedRowIndexes = new Set<number>();

  constructor(
    private router: Router,
    private notificationService: NotificationService,
    private largeChartViewService: LargeChartViewService,
    private chartInsightsDrilldown: ChartInsightsDrilldownService,
    private confirmPopupService: ConfirmPopupService
  ) {}

  ngOnInit() {
    // Get chart data from service
    this.chartData = this.largeChartViewService.getChartData();

    if (!this.chartData) {
      // If no chart data, try to get it from observable
      this.largeChartViewService.chartData$.subscribe(data => {
        if (data) {
          this.chartData = data;
          this.initializeChart();
        } else {
          // No chart data available, navigate back
          this.router.navigate(['user-dashboard']);
        }
      });
    } else {
      this.initializeChart();
    }
  }

  ngOnChanges(changes: SimpleChanges) {
    if (changes['chartData'] && this.chartData) {
      this.updateChart();
    }
  }

  initializeChart() {
    if (this.chartData) {
this.updateChart();
    }
  }

  updateChart() {
    if (!this.chartData) {
      this.hasError = true;
      this.errorMessage = 'No chart data available';
      return;
    }

    if (!this.chartData.data) {
      this.hasError = true;
      this.errorMessage = 'No chart data available';
      this.chartOptions = {};
      return;
    }

    // Check if no_data_found flag is set to true
    if (this.chartData.no_data_found === true) {
      this.hasError = true;
      this.errorMessage = 'No data found';
      this.chartOptions = {};
      // Show notification for no data found
      this.notificationService.info('No Data', `No data found for chart: ${this.chartData.title}`);
      return;
    }

    try {
      this.hasError = false;
      this.errorMessage = '';

      const chartType = this.chartData.chart_type.toLowerCase();

      // If chart_config is available from API, use it directly (it's ready to use)
      // But for line/area charts we must use normalized data so timestamps and series type render correctly
      const isLineOrAreaChart = ['line', 'spline', 'area', 'areaspline'].includes(chartType);
      const hasValidConfig = this.chartData.config && this.chartData.config.chart && this.chartData.config.series;

      if (hasValidConfig && !isLineOrAreaChart) {
        // Use subtitle from API as-is (no client-side formatting)
        const subtitleText = this.chartData.config.subtitle?.text ?? '';

        // Use chart_config from API, but ensure title is empty (shown in HTML header)
        const apiCfg = normalizeHighchartsApiConfig(
          { ...this.chartData.config },
          this.chartData.chart_type
        );
        this.chartOptions = {
          ...apiCfg,
          title: {
            text: ''
          },
          subtitle: {
            text: subtitleText
          },
          credits: {
            enabled: false
          },
          exporting: this.getExportingConfig(),
          lang: {
            ...(apiCfg.lang || {}),
            thousandsSep: ''
          }
        };
        return;
      }

      // Use data.data if available, otherwise use data; pass chartType for correct series type
      const chartDataForNormalization = this.chartData.data || this.chartData;
      const normalizedData = this.normalizeData(chartDataForNormalization, chartType);

      // Check if data is empty after normalization
      if (this.isDataEmpty(normalizedData)) {
        this.hasError = true;
        this.errorMessage = 'No data found';
        this.chartOptions = {};
        return;
      }

      switch (chartType) {
        case 'bar':
          this.chartOptions = this.createBarChartOptions(normalizedData);
          break;
        case 'column':
          this.chartOptions = this.createColumnChartOptions(normalizedData);
          break;
        case 'line':
        case 'spline':
          this.chartOptions = this.createLineChartOptions(normalizedData, chartType);
          break;
        case 'pie':
          this.chartOptions = this.createPieChartOptions(normalizedData);
          break;
        case 'donut':
          this.chartOptions = this.createDonutChartOptions(normalizedData);
          break;
        case 'area':
        case 'areaspline':
          this.chartOptions = this.createAreaChartOptions(normalizedData, chartType);
          break;
        case 'scatter':
          this.chartOptions = this.createScatterChartOptions(normalizedData);
          break;
        case 'gauge':
          this.chartOptions = this.createGaugeChartOptions(normalizedData);
          break;
        case 'heatmap':
          this.chartOptions = this.createHeatmapChartOptions(normalizedData);
          break;
        case 'treemap':
          this.chartOptions = this.createTreemapChartOptions(normalizedData);
          break;
        case 'sankey':
          this.chartOptions = this.createSankeyChartOptions(normalizedData);
          break;
        case 'bubble':
          this.chartOptions = this.createBubbleChartOptions(normalizedData);
          break;
        case 'waterfall':
          this.chartOptions = this.createWaterfallChartOptions(normalizedData);
          break;
        case 'polar':
          this.chartOptions = this.createPolarChartOptions(normalizedData);
          break;
        default:
          // console.warn(`Unknown chart type: ${chartType}, defaulting to column chart`);
          this.chartOptions = this.createColumnChartOptions(normalizedData);
      }
    } catch (error: any) {
      console.error('Error updating chart:', error);
      this.hasError = true;
      this.errorMessage = error.message || 'Failed to render chart';
    }
  }

  isDataEmpty(normalizedData: any): boolean {
    // Check if series exists and has data
    if (!normalizedData.series || normalizedData.series.length === 0) {
      return true;
    }

    // Check if any series has actual data points
    const hasDataPoints = normalizedData.series.some((series: any) => {
      if (!series.data || !Array.isArray(series.data)) {
        return false;
      }
      return series.data.length > 0;
    });

    return !hasDataPoints;
  }

  normalizeData(data: any, chartType?: string): any {
    // Handle different API response formats
    let normalized: any = {
      categories: [],
      series: [],
      data: [],
      xAxisLabel: '',
      yAxisLabel: ''
    };

    // If data is already normalized, return as is
    if (data.categories && data.series) {
      return data;
    }

    const seriesTypeDefault = chartType || 'line';

    if (Array.isArray(data.categories)) {
      normalized.categories = normalizeCategoryArray(data.categories);
    } else if (data.x_axis && Array.isArray(data.x_axis)) {
      normalized.categories = normalizeCategoryArray(data.x_axis);
    } else if (data.labels && Array.isArray(data.labels)) {
      normalized.categories = normalizeCategoryArray(data.labels);
    }

    // Check if this is a time series chart (line, spline, area, areaspline with timestamp data)
    const isTimeSeriesChart = (seriesData: any[]): boolean => {
      if (!Array.isArray(seriesData) || seriesData.length === 0) {
        return false;
      }
      const firstItem = seriesData[0];
      // Check if first item is [timestamp, value] format
      if (Array.isArray(firstItem) && firstItem.length >= 2) {
        const timestamp = firstItem[0];
        // If timestamp is a string that looks like a date, it's time series
        if (typeof timestamp === 'string' && (timestamp.includes('-') || timestamp.includes('T'))) {
          return true;
        }
      }
      return false;
    };

    // Extract series - handle different data formats based on chart type
    const cleanSeriesData = (seriesData: any[], isTimeSeries: boolean = false): any[] => {
      if (!Array.isArray(seriesData)) {
        return [];
      }

      return seriesData.filter((item: any) => {
        // For time series: [["timestamp", value], ...]
        if (isTimeSeries && Array.isArray(item) && item.length >= 2) {
          return item[1] !== null && item[1] !== undefined;
        }
        // For pie/donut: [{"name": "...", "y": value}, ...] or [["name", value], ...]
        if (Array.isArray(item) && item.length >= 2) {
          return item[1] !== null && item[1] !== undefined;
        }
        // For objects with y or value properties
        if (typeof item === 'object' && item !== null) {
          return (item.y !== null && item.y !== undefined) || (item.value !== null && item.value !== undefined);
        }
        // For simple values
        return item !== null && item !== undefined;
      }).map((item: any) => {
        // Handle time series format: [["timestamp", value], ...]
        if (isTimeSeries && Array.isArray(item) && item.length >= 2) {
          const timestamp = item[0];
          let value = item[1];
          // Convert timestamp string to number (milliseconds since epoch)
          if (typeof timestamp === 'string') {
            const date = new Date(timestamp);
            if (!isNaN(date.getTime())) {
              return [date.getTime(), typeof value === 'string' ? parseFloat(value) || 0 : (value || 0)];
            }
          }
          // If timestamp is already a number, use it directly
          return [typeof timestamp === 'string' ? parseFloat(timestamp) || 0 : timestamp,
                  typeof value === 'string' ? parseFloat(value) || 0 : (value || 0)];
        }
        // Handle pie/donut format: [["name", value], ...]
        if (Array.isArray(item) && item.length >= 2) {
          const name = normalizeChartDisplayLabel(item[0]);
          const value = typeof item[1] === 'string' ? parseFloat(item[1]) || 0 : (item[1] || 0);
          return [name, value];
        }
        // Handle pie/donut format: [{"name": "...", "y": value}, ...]
        if (typeof item === 'object' && item !== null) {
          const name = normalizeChartDisplayLabel(item.name ?? item.label ?? '');
          const value = item.y !== undefined ? item.y : item.value;
          const numValue = typeof value === 'string' ? parseFloat(value) || 0 : (value || 0);
          return { name, y: numValue };
        }
        // Convert simple string numbers to actual numbers
        if (typeof item === 'string' && !isNaN(parseFloat(item)) && isFinite(parseFloat(item))) {
          return parseFloat(item);
        }
        // Keep numbers as-is
        if (typeof item === 'number') {
          return item;
        }
        return item;
      });
    };

    if (Array.isArray(data.series)) {
      normalized.series = data.series.map((s: any) => {
        if (typeof s === 'object' && s !== null) {
          const seriesData = Array.isArray(s.data) ? s.data : (s.values || []);
          const isTimeSeries = isTimeSeriesChart(seriesData);
          return {
            name: normalizeChartDisplayLabel(s.name ?? s.label ?? ''),
            type: (typeof s.type === 'string' && s.type.length > 0) ? s.type : seriesTypeDefault,
            data: cleanSeriesData(seriesData, isTimeSeries),
            color: s.color,
            yAxis: s.yAxis
          };
        }
        return s;
      });
    } else if (data.data && Array.isArray(data.data)) {
      // Single series format
      const isTimeSeries = isTimeSeriesChart(data.data);
      normalized.series = [{
        name: 'Data',
        type: seriesTypeDefault,
        data: cleanSeriesData(data.data, isTimeSeries)
      }];
    } else if (data.values && Array.isArray(data.values)) {
      const isTimeSeries = isTimeSeriesChart(data.values);
      normalized.series = [{
        name: 'Data',
        type: seriesTypeDefault,
        data: cleanSeriesData(data.values, isTimeSeries)
      }];
    } else if (typeof data === 'object' && data !== null) {
      const seriesKeys = Object.keys(data).filter(key =>
        !['categories', 'x_axis', 'labels', 'xAxisLabel', 'yAxisLabel', 'chart_id', 'id', 'table_data'].includes(key)
      );

      if (seriesKeys.length > 0) {
        normalized.series = seriesKeys.map(key => {
          const seriesData = Array.isArray(data[key]) ? data[key] : [];
          const isTimeSeries = isTimeSeriesChart(seriesData);
          return {
            name: normalizeChartDisplayLabel(key),
            type: seriesTypeDefault,
            data: cleanSeriesData(seriesData, isTimeSeries)
          };
        });
      }
    }

    normalized.xAxisLabel = data.xAxisLabel || data.x_axis_label || data.x_label || '';
    normalized.yAxisLabel = data.yAxisLabel || data.y_axis_label || data.y_label || 'Value';

    if (data.data && Array.isArray(data.data)) {
      normalized.data = data.data;
    } else if (data.values && Array.isArray(data.values)) {
      normalized.data = data.values;
    } else if (normalized.series && normalized.series.length > 0 && normalized.series[0].data) {
      normalized.data = normalized.series[0].data;
    }

    return normalized;
  }

  createBarChartOptions(data: any): Highcharts.Options {
    // Format data for table display with name, count, percentage structure
    const formattedData = this.formatBarColumnData(data.series?.[0]?.data || data.data || []);

    // Update table_data if it exists
    if (this.chartData && this.chartData.data) {
      this.chartData.data.table_data = formattedData;
    }

    return {
      chart: {
        type: 'bar',
        height: null, // Let container control height
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      xAxis: {
        categories: data.categories || [],
        title: {
          text: data.xAxisLabel || ''
        },
        labels: {
          style: {
            textOverflow: 'none',
            whiteSpace: 'nowrap'
          },
          autoRotation: [],
          step: 0
        }
      },
      yAxis: {
        title: {
          text: data.yAxisLabel || 'Value'
        },
        labels: {
          formatter: function() {
            return this.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          }
        }
      },
      series: this.ensureSeriesFormat(data.series || []),
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          fontSize: '14px'
        },
        itemHoverStyle: {
          color: '#000'
        }
      },
      tooltip: {
        shared: false
      }
    };
  }

  createColumnChartOptions(data: any): Highcharts.Options {
    // Format data for table display with name, count, percentage structure
    const formattedData = this.formatBarColumnData(data.series?.[0]?.data || data.data || []);

    // Update table_data if it exists
    if (this.chartData && this.chartData.data) {
      this.chartData.data.table_data = formattedData;
    }

    return {
      chart: {
        type: 'column',
        height: null, // Let container control height
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      xAxis: {
        categories: data.categories || [],
        title: {
          text: data.xAxisLabel || ''
        },
        labels: {
          style: {
            textOverflow: 'none',
            whiteSpace: 'nowrap'
          },
          autoRotation: [],
          step: 0
        }
      },
      yAxis: {
        title: {
          text: data.yAxisLabel || 'Value'
        },
        labels: {
          formatter: function() {
            return this.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          }
        }
      },
      series: this.ensureSeriesFormat(data.series || []),
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          fontSize: '14px'
        },
        itemHoverStyle: {
          color: '#000'
        }
      },
      tooltip: {
        shared: false
      }
    };
  }

  createLineChartOptions(data: any, chartType: string = 'line'): Highcharts.Options {
    // Check if series data is time series format (nested arrays with timestamps)
    const isTimeSeries = data.series && data.series.length > 0 &&
                        data.series[0].data &&
                        Array.isArray(data.series[0].data) &&
                        data.series[0].data.length > 0 &&
                        Array.isArray(data.series[0].data[0]) &&
                        typeof data.series[0].data[0][0] === 'number';

    return {
      chart: {
        type: chartType as any,
        height: null, // Let container control height
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      xAxis: isTimeSeries ? {
        type: 'datetime',
        title: {
          text: data.xAxisLabel || 'Date'
        },
        labels: {
          style: {
            textOverflow: 'none',
            whiteSpace: 'nowrap'
          },
          autoRotation: [],
          step: 0
        }
      } : {
        categories: data.categories || [],
        title: {
          text: data.xAxisLabel || ''
        },
        labels: {
          style: {
            textOverflow: 'none',
            whiteSpace: 'nowrap'
          },
          autoRotation: [],
          step: 0
        }
      },
      yAxis: {
        title: {
          text: data.yAxisLabel || 'Value'
        },
        labels: {
          formatter: function() {
            return this.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          }
        }
      },
      series: this.ensureSeriesFormat(data.series || []),
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          fontSize: '14px'
        },
        itemHoverStyle: {
          color: '#000'
        }
      },
      tooltip: {
        shared: true
      }
    };
  }

  createPieChartOptions(data: any): Highcharts.Options {
    const pieData = data.series?.[0]?.data || data.data || [];
    return {
      chart: {
        type: 'pie',
        height: null, // Let container control height
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      series: [{
        type: 'pie',
        name: data.series?.[0]?.name || 'Data',
        data: this.formatPieData(pieData)
      }],
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          fontSize: '14px'
        },
        itemHoverStyle: {
          color: '#000'
        }
      },
      tooltip: {
        pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>'
      }
    };
  }

  createDonutChartOptions(data: any): Highcharts.Options {
    const pieData = data.series?.[0]?.data || data.data || [];
    return {
      chart: {
        type: 'pie',
        height: null, // Let container control height
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      plotOptions: {
        pie: {
          innerSize: '50%'
        }
      },
      series: [{
        type: 'pie',
        name: data.series?.[0]?.name || 'Data',
        data: this.formatPieData(pieData)
      }],
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          fontSize: '14px'
        },
        itemHoverStyle: {
          color: '#000'
        }
      },
      tooltip: {
        pointFormat: '{series.name}: <b>{point.percentage:.1f}%</b>'
      }
    };
  }

  createAreaChartOptions(data: any, chartType: string = 'area'): Highcharts.Options {
    // Check if series data is time series format (nested arrays with timestamps)
    const isTimeSeries = data.series && data.series.length > 0 &&
                        data.series[0].data &&
                        Array.isArray(data.series[0].data) &&
                        data.series[0].data.length > 0 &&
                        Array.isArray(data.series[0].data[0]) &&
                        typeof data.series[0].data[0][0] === 'number';

    return {
      chart: {
        type: chartType as any,
        height: null, // Let container control height
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      xAxis: isTimeSeries ? {
        type: 'datetime',
        title: {
          text: data.xAxisLabel || 'Date'
        },
        labels: {
          style: {
            textOverflow: 'none',
            whiteSpace: 'nowrap'
          },
          autoRotation: [],
          step: 0
        }
      } : {
        categories: data.categories || [],
        title: {
          text: data.xAxisLabel || ''
        },
        labels: {
          style: {
            textOverflow: 'none',
            whiteSpace: 'nowrap'
          },
          autoRotation: [],
          step: 0
        }
      },
      yAxis: {
        title: {
          text: data.yAxisLabel || 'Value'
        },
        labels: {
          formatter: function() {
            return this.value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
          }
        }
      },
      series: this.ensureSeriesFormat(data.series || [], chartType),
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          fontSize: '14px'
        },
        itemHoverStyle: {
          color: '#000'
        }
      },
      plotOptions: {
        area: {
          stacking: 'normal'
        }
      },
      tooltip: {
        shared: true
      }
    };
  }

  createScatterChartOptions(data: any): Highcharts.Options {
    return {
      chart: {
        type: 'scatter',
        height: null, // Let container control height
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      xAxis: {
        title: {
          text: data.xAxisLabel || 'X Axis'
        },
        type: 'linear'
      },
      yAxis: {
        title: {
          text: data.yAxisLabel || 'Y Axis'
        }
      },
      series: this.ensureSeriesFormat(data.series || []),
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          fontSize: '14px'
        },
        itemHoverStyle: {
          color: '#000'
        }
      },
      tooltip: {
        pointFormat: 'X: {point.x}<br/>Y: {point.y}'
      }
    };
  }

  createGaugeChartOptions(data: any): Highcharts.Options {
    const value = this.extractGaugeValue(data);
    // Try to get min/max from yAxis config if available
    const min = data.yAxis?.min !== undefined ? data.yAxis.min : 0;
    const max = data.yAxis?.max !== undefined ? data.yAxis.max : 100;

    return {
      chart: {
        type: 'gauge',
        height: null, // Let container control height
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      pane: {
        startAngle: -90,
        endAngle: 90
      },
      yAxis: {
        min: min,
        max: max,
        title: {
          text: data.yAxisLabel || 'Value'
        }
      },
      series: [{
        type: 'gauge',
        name: data.series?.[0]?.name || 'Value',
        data: [value],
        tooltip: {
          valueSuffix: ''
        }
      }],
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          fontSize: '14px'
        }
      },
      plotOptions: {
        gauge: {
          dial: {
            radius: '100%',
            backgroundColor: 'gray',
            baseWidth: 12,
            baseLength: '0%',
            rearLength: '0%'
          },
          pivot: {
            backgroundColor: 'gray'
          }
        }
      }
    };
  }

  createHeatmapChartOptions(data: any): Highcharts.Options {
    const heatmapData = this.formatHeatmapData(data);

    // Extract unique dimensions for x and y axes
    const xCategories = new Set<string>();
    const yCategories = new Set<string>();

    heatmapData.forEach((item: any) => {
      if (Array.isArray(item) && item.length >= 3) {
        xCategories.add(String(item[0]));
        yCategories.add(String(item[1]));
      }
    });

    return {
      chart: {
        type: 'heatmap',
        height: null, // Let container control height
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      colorAxis: {
        min: 0,
        minColor: '#FFFFFF',
        maxColor: '#0000FF'
      },
      xAxis: {
        categories: Array.from(xCategories),
        title: {
          text: data.xAxisLabel || ''
        }
      },
      yAxis: {
        categories: Array.from(yCategories),
        title: {
          text: data.yAxisLabel || ''
        }
      },
      series: [{
        type: 'heatmap',
        name: data.series?.[0]?.name || 'Data',
        data: heatmapData
      }],
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          fontSize: '14px'
        },
        itemHoverStyle: {
          color: '#000'
        }
      },
      tooltip: {
        formatter: function() {
          const point = this.point as any;
          const xCat = point.series.xAxis.categories[point.x];
          const yCat = point.series.yAxis.categories[point.y];
          return `<b>${xCat}</b><br><b>${yCat}</b><br>Value: <b>${point.value}</b>`;
        }
      }
    };
  }

  createTreemapChartOptions(data: any): Highcharts.Options {
    return {
      chart: {
        type: 'treemap',
        height: null, // Let container control height
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      series: [{
        type: 'treemap',
        name: data.series?.[0]?.name || 'Data',
        data: this.formatTreemapData(data)
      }],
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          fontSize: '14px'
        },
        itemHoverStyle: {
          color: '#000'
        }
      },
      tooltip: {
        pointFormat: '<b>{point.name}</b>: {point.value}'
      }
    };
  }

  createSankeyChartOptions(data: any): Highcharts.Options {
    return {
      chart: {
        type: 'sankey',
        height: null, // Let container control height
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      colors: SENTIMENT_SANKEY_COLORS,
      plotOptions: {
        sankey: {
          colors: SENTIMENT_SANKEY_COLORS
        }
      },
      series: [{
        type: 'sankey',
        name: 'Flow',
        keys: ['from', 'to', 'weight'],
        data: this.formatSankeyData(data),
        nodes: data.nodes || [],
        colorByPoint: true,
        colors: SENTIMENT_SANKEY_COLORS
      }],
      tooltip: {
        useHTML: true,
        formatter: function (this: Highcharts.TooltipFormatterContextObject) {
          const point = this.point as any;
          const fromName = point.from || point.fromNode?.name || '';
          const toName = point.to || point.toNode?.name || '';
          const pctRaw = point.percent ?? point.options?.percent;
          const pct =
            typeof pctRaw === 'number' && !isNaN(pctRaw)
              ? pctRaw
              : pctRaw != null && !isNaN(Number(pctRaw))
                ? Number(pctRaw)
                : undefined;
          const w = point.weight ?? point.y;
          if (pct !== undefined) {
            const pctStr =
              pct.toLocaleString(undefined, {
                minimumFractionDigits: 0,
                maximumFractionDigits: 2,
                useGrouping: false,
              }) + '%';
            return (
              `<div style="font-family:Lexend,sans-serif">` +
              `<div style="font-size:11px;color:#6b7280;font-weight:600;">${fromName} → ${toName}</div>` +
              `<div style="font-size:15px;font-weight:700;margin-top:4px">${pctStr}</div>` +
              `</div>`
            );
          }
          const val =
            typeof w === 'number'
              ? w.toLocaleString(undefined, { useGrouping: false })
              : String(w ?? '');
          return `<b>${fromName} → ${toName}</b><br/>${val}`;
        },
      },
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          fontSize: '14px'
        },
        itemHoverStyle: {
          color: '#000'
        }
      }
    };
  }

  createBubbleChartOptions(data: any): Highcharts.Options {
    return {
      chart: {
        type: 'bubble',
        height: null, // Let container control height
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      xAxis: {
        title: {
          text: data.xAxisLabel || 'X Axis'
        }
      },
      yAxis: {
        title: {
          text: data.yAxisLabel || 'Y Axis'
        }
      },
      series: this.ensureSeriesFormat(data.series || [], 'bubble'),
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          fontSize: '14px'
        },
        itemHoverStyle: {
          color: '#000'
        }
      },
      tooltip: {
        pointFormat: 'X: {point.x}<br/>Y: {point.y}<br/>Size: {point.z}'
      }
    };
  }

  createWaterfallChartOptions(data: any): Highcharts.Options {
    return {
      chart: {
        type: 'waterfall',
        height: null, // Let container control height
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      xAxis: {
        categories: data.categories || [],
        title: {
          text: data.xAxisLabel || ''
        }
      },
      yAxis: {
        title: {
          text: data.yAxisLabel || 'Value'
        }
      },
      series: this.ensureSeriesFormat(data.series || []),
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          fontSize: '14px'
        },
        itemHoverStyle: {
          color: '#000'
        }
      }
    };
  }

  createPolarChartOptions(data: any): Highcharts.Options {
    return {
      chart: {
        type: 'line',
        polar: true,
        height: null, // Let container control height
        backgroundColor: 'transparent'
      },
      title: {
        text: ''
      },
      credits: {
        enabled: false
      },
      exporting: this.getExportingConfig(),
      xAxis: {
        categories: data.categories || [],
        tickmarkPlacement: 'on',
        lineWidth: 0
      },
      yAxis: {
        gridLineInterpolation: 'polygon',
        lineWidth: 0,
        min: 0
      },
      series: this.ensureSeriesFormat(data.series || []),
      legend: {
        enabled: true,
        layout: 'horizontal',
        align: 'center',
        verticalAlign: 'bottom',
        itemStyle: {
          fontSize: '14px'
        },
        itemHoverStyle: {
          color: '#000'
        }
      }
    };
  }

  getExportingConfig(): Highcharts.ExportingOptions {
    return {
      enabled: false,
      menuItemDefinitions: {
        // viewFullscreen: {
        //   text: 'View in Full Screen'
        // },
        // printChart: {
        //   text: 'Print Chart'
        // },
        // downloadPNG: {
        //   text: 'Download as PNG'
        // },
        downloadJPEG: {
          text: 'Download Image'
        },
        // downloadPDF: {
        //   text: 'Download PDF'
        // },
        // downloadSVG: {
        //   text: 'Download as SVG'
        // },
        // downloadCSV: {
        //   text: 'Download Chart Table'
        // },
        // downloadXLS: {
        //   text: 'Download Excel File'
        // },
        // viewData: {
        //   text: 'View Data Table'
        // }
      },
      buttons: {
        contextButton: {
          enabled: false,
          menuItems: [
            // 'viewFullscreen',
            // 'printChart',
            // 'separator',
            // 'downloadPNG',
            'downloadJPEG',
            // 'downloadPDF',
            // 'downloadSVG',
            // 'separator',
            // 'downloadCSV',
            // 'downloadXLS',
            // 'viewData'
          ],
          x: -10,
          y: 5,
          symbol: 'menu',
          symbolStrokeWidth: 2
        }
      }
    };
  }

  ensureSeriesFormat(series: any[], chartType?: string): any[] {
    if (!Array.isArray(series)) {
      return [];
    }
    const typeDefault = chartType || 'line';
    return series.map(s => {
      if (typeof s === 'object' && s !== null) {
        let data = Array.isArray(s.data) ? s.data : (s.values || []);

        // Special handling for bubble charts - ensure data has x, y, z format
        if (chartType === 'bubble' || s.type === 'bubble') {
          data = data.map((item: any) => {
            if (Array.isArray(item) && item.length >= 3) {
              // Already in correct format [x, y, z]
              return item;
            } else if (typeof item === 'object' && item !== null) {
              // Convert object format {x: val, y: val, z: val} to array
              return [
                item.x !== undefined ? item.x : 0,
                item.y !== undefined ? item.y : 0,
                item.z !== undefined ? item.z : (item.value || 1)
              ];
            } else if (Array.isArray(item) && item.length === 2) {
              // Add default z value if missing
              return [item[0], item[1], 1];
            }
            // Fallback
            return [0, 0, 1];
          });
        }

        return {
          ...s,
          name: normalizeChartDisplayLabel(s.name ?? ''),
          type: (typeof s.type === 'string' && s.type.length > 0) ? s.type : typeDefault,
          data: data
        };
      }
      return s;
    });
  }

  formatPieData(data: any[]): any[] {
    if (!Array.isArray(data)) {
      return [];
    }
    return data.map(item => {
      // Handle pie/donut format: [{"name": "...", "y": value}, ...]
      if (typeof item === 'object' && item !== null) {
        const name = normalizeChartDisplayLabel(item.name ?? item.label ?? '');
        const value = item.y !== undefined ? item.y : item.value;
        const numValue = typeof value === 'string' ? parseFloat(value) || 0 : (value || 0);
        return { name, y: numValue };
      }
      // Handle pie/donut format: [["name", value], ...]
      if (Array.isArray(item) && item.length >= 2) {
        const name = normalizeChartDisplayLabel(item[0]);
        const numValue = typeof item[1] === 'string' ? parseFloat(item[1]) || 0 : (item[1] || 0);
        return { name, y: numValue };
      }
      // Fallback for simple values
      const numValue = typeof item === 'string' ? parseFloat(item) || 0 : (item || 0);
      return { name: normalizeChartDisplayLabel(''), y: numValue };
    });
  }

  formatBarColumnData(data: any[]): any[] {
    if (!Array.isArray(data)) {
      return [];
    }
    return data.map(item => {
      // Handle format: [{"name": "...", "y": value}, ...] or [{"name": "...", "count": value, "percentage": value}, ...]
      if (typeof item === 'object' && item !== null) {
        const name = normalizeChartDisplayLabel(item.name ?? item.label ?? '');
        let count, percentage;

        if (item.count !== undefined && item.percentage !== undefined) {
          // Already in the correct format: name, count, percentage
          count = typeof item.count === 'string' ? parseFloat(item.count) || 0 : (item.count || 0);
          percentage = typeof item.percentage === 'string' ? parseFloat(item.percentage) || 0 : (item.percentage || 0);
        } else {
          // Convert from y/value to count and calculate percentage
          const value = item.y !== undefined ? item.y : item.value;
          count = typeof value === 'string' ? parseFloat(value) || 0 : (value || 0);
          percentage = count; // Will be calculated later if needed
        }

        return { name, count, percentage };
      }
      // Handle array format: [["name", value], ...]
      if (Array.isArray(item) && item.length >= 2) {
        const name = normalizeChartDisplayLabel(item[0]);
        const count = typeof item[1] === 'string' ? parseFloat(item[1]) || 0 : (item[1] || 0);
        return { name, count, percentage: count };
      }
      // Fallback for simple values
      const count = typeof item === 'string' ? parseFloat(item) || 0 : (item || 0);
      return { name: normalizeChartDisplayLabel(''), count, percentage: count };
    });
  }

  formatHeatmapData(data: any): any[] {
    // Heatmap data format: [["dim1", "dim2", value], ...]
    const heatmapData = data.series?.[0]?.data || data.data || [];
    if (!Array.isArray(heatmapData)) {
      return [];
    }
    // Heatmap data should already be in the correct format from API
    // Just ensure values are numbers
    return heatmapData.map((item: any) => {
      if (Array.isArray(item) && item.length >= 3) {
        const dim1 = normalizeChartDisplayLabel(item[0]);
        const dim2 = normalizeChartDisplayLabel(item[1]);
        const value = typeof item[2] === 'string' ? parseFloat(item[2]) || 0 : (item[2] || 0);
        return [dim1, dim2, value];
      }
      return item;
    });
  }

  formatTreemapData(data: any): any[] {
    // Treemap data format: [{name: 'A', value: 1}, ...]
    const treemapData = data.series?.[0]?.data || data.data || [];
    if (!Array.isArray(treemapData)) {
      return [];
    }
    return treemapData.map((item: any) => {
      // Handle treemap format: [{"name": "...", "value": value}, ...]
      if (typeof item === 'object' && item !== null) {
        const name = normalizeChartDisplayLabel(item.name ?? '');
        const value = item.value !== undefined ? item.value : item.y;
        const numValue = typeof value === 'string' ? parseFloat(value) || 0 : (value || 0);
        return { name, value: numValue };
      }
      // Handle array format: [["name", value], ...]
      if (Array.isArray(item) && item.length >= 2) {
        const name = normalizeChartDisplayLabel(item[0]);
        const numValue = typeof item[1] === 'string' ? parseFloat(item[1]) || 0 : (item[1] || 0);
        return { name, value: numValue };
      }
      return { name: normalizeChartDisplayLabel(''), value: 0 };
    });
  }

  formatSankeyData(data: any): any[] {
    if (Array.isArray(data.links)) {
      return data.links.map((link: any) => {
        const from = normalizeChartDisplayLabel(link.source ?? link.from ?? '');
        const to = normalizeChartDisplayLabel(link.target ?? link.to ?? '');
        const weight = Number(link.value ?? link.weight ?? 0) || 0;
        const percentRaw = link.percent ?? link.Percent ?? link.percentage ?? link.Percentage;
        if (percentRaw !== undefined && percentRaw !== null && !isNaN(Number(percentRaw))) {
          return { from, to, weight, percent: Number(percentRaw) };
        }
        return [from, to, link.value ?? link.weight ?? 0];
      });
    }
    if (Array.isArray(data.data)) {
      return data.data.map((row: any) => {
        if (Array.isArray(row) && row.length >= 3) {
          return [
            normalizeChartDisplayLabel(row[0]),
            normalizeChartDisplayLabel(row[1]),
            ...row.slice(2)
          ];
        }
        return row;
      });
    }
    return [];
  }

  extractGaugeValue(data: any): number {
    // Gauge format: [value] - single value array
    if (data.series && data.series[0] && data.series[0].data) {
      const seriesData = data.series[0].data;
      if (Array.isArray(seriesData) && seriesData.length > 0) {
        const value = seriesData[0];
        return typeof value === 'string' ? parseFloat(value) || 0 : (Number(value) || 0);
      }
      return Number(seriesData) || 0;
    }
    if (data.data && Array.isArray(data.data) && data.data.length > 0) {
      const value = data.data[0];
      return typeof value === 'string' ? parseFloat(value) || 0 : (Number(value) || 0);
    }
    if (data.value !== undefined) {
      return typeof data.value === 'string' ? parseFloat(data.value) || 0 : (Number(data.value) || 0);
    }
    return 0;
  }

  onClose() {
    this.largeChartViewService.clearChartData();

    // Try to get the last viewed dashboard ID from localStorage
    const lastViewedDashboardId = sessionStorage.getItem('lastViewedDashboardId');

    if (lastViewedDashboardId) {
      // Navigate back to the specific dashboard
      this.router.navigate(['create-dashboard'], {
        queryParams: { id: lastViewedDashboardId }
      });
      // Clear the stored dashboard ID after using it
      sessionStorage.removeItem('lastViewedDashboardId');
    } else {
      // Fallback to dashboard list if no specific dashboard was stored
      this.router.navigate(['user-dashboard']);
    }
  }

  onDownload() {
    if (!this.chartData) {
      console.error('Chart data not available for CSV export');
      this.notificationService.error('Error', 'Chart data not available. Cannot export to CSV.');
      return;
    }

    if (downloadChartTableCsv(this.chartData)) {
      this.notificationService.success('Download Complete', 'CSV file download started');
      return;
    }

    this.notificationService.error(
      'Nothing to export',
      'This chart has no table data loaded. Try another chart or adjust filters.'
    );
  }


  getSubtitle(): string | null {
    // Get subtitle from config.subtitle.text
    if (this.chartData?.config?.subtitle?.text) {
      return this.chartData.config.subtitle.text;
    }
    return null;
  }

  getDuration(): string | null {
    // Return subtitle from API as-is
    return this.getSubtitle();
  }

  formatUTCDateTime(date: Date): string {
    // Format in the chart's selected timezone, e.g. "Jan 2, 2026 18:29:59 EST"
    return formatDateTimeInTimezone(date, this.getChartTimezone());
  }

  /** Timezone selected for this chart (e.g. EST). Used for subtitle and Last updated. */
  getChartTimezone(): string {
    if (!this.chartData) return 'EST';
    const c = this.chartData as any;
    const tz = c?.time_zone ?? this.chartData?.config?.time_zone ?? c?.criteria?.time_zone ?? c?.TimeZone;
    return (tz && String(tz).trim()) ? String(tz).trim() : 'EST';
  }

  formatUserFriendlyDateTime(date: Date): string {
    // Format in the chart's selected timezone, e.g. "10 Feb 2025 3:21:28 PM EST"
    return formatUserFriendlyDateTimeInTimezone(date, this.getChartTimezone());
  }

  formatSubtitleText(subtitleText: string): string {
    // Parse subtitle format: "From 2022-12-31T18:30:00Z to 2026-01-08T18:29:59.999000Z"
    // or "2022-12-31T18:30:00Z to 2026-01-08T18:29:59.999000Z"
    const dateRangeMatch = subtitleText.match(/(\d{4}-\d{2}-\d{2}T[\d:\.Z]+)\s+to\s+(\d{4}-\d{2}-\d{2}T[\d:\.Z]+)/i);
    if (dateRangeMatch) {
      try {
        const startDate = new Date(dateRangeMatch[1].trim());
        const endDate = new Date(dateRangeMatch[2].trim());

        if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
          const startFormatted = this.formatUserFriendlyDateTime(startDate);
          const endFormatted = this.formatUserFriendlyDateTime(endDate);
          // Check if "From" is already in the original text
          const hasFromPrefix = /^from\s+/i.test(subtitleText);
          return hasFromPrefix
            ? `From ${startFormatted} to ${endFormatted}`
            : `${startFormatted} to ${endFormatted}`;
        }
      } catch (error) {
        // console.warn('Error parsing subtitle date range:', error);
      }
    }
    // If parsing fails, return original text
    return subtitleText;
  }

  getLastUpdatedTime(): string {
    // Prefer last_updated_timestamp (data refresh / LastGeneratedAt) for both regular and fixed ranges
    const timestamp = this.chartData?.last_updated_timestamp
      || this.chartData?.updated_at
      || this.chartData?.generated_at;

    if (timestamp) {
      try {
        const date = new Date(timestamp);
        if (!isNaN(date.getTime())) {
          return this.formatUTCDateTime(date);
        }
      } catch (error) {
        console.warn('Error parsing timestamp:', error);
      }
    }

    // Fallback if timestamp is not available or invalid
    return '';
  }

  getLastEditedTime(): string {
    const timestamp = this.chartData?.last_edited_at
      || this.chartData?.updated_at
      || this.chartData?.generated_at;

    if (!timestamp) {
      return '';
    }

    try {
      const date = new Date(timestamp);
      if (!isNaN(date.getTime())) {
        return this.formatUTCDateTime(date);
      }
    } catch (error) {
      console.warn('Error parsing last edited timestamp:', error);
    }

    return '';
  }

  getLastEditedBy(): string {
    const chartDataAny = this.chartData as any;
    const editor = this.chartData?.updated_by
      || chartDataAny?.updated_by_email
      || this.chartData?.generated_by
      || chartDataAny?.generated_by_email
      || chartDataAny?.CreatedByEmail
      || chartDataAny?.UpdatedByEmail;

    return formatDashboardUserEmail(editor);
  }

  getDurationBehavior(): string | null {
    // Get DurationBehavior from chart data (from API response)
    if (!this.chartData) {
      return null;
    }

    // Check multiple possible locations for DurationBehavior/chart_duration_tag
    const chartDataAny = this.chartData as any;
    const durationBehavior = this.chartData.DurationBehavior
      || chartDataAny.chart_duration_tag
      || chartDataAny.duration_behavior
      || chartDataAny.durationBehavior
      || this.chartData.config?.chart_duration_tag
      || this.chartData.config?.DurationBehavior
      || (this.chartData.config as any)?.duration_behavior
      || (this.chartData.config as any)?.durationBehavior;

    return durationBehavior || null;
  }

  getDurationBehaviorLabel(): string | null {
    const behavior = this.getDurationBehavior();
    if (!behavior) {
      return null;
    }

    // Map DurationBehavior values to short, concise labels
    // Handle both existing API format (dynamic_monthly) and dropdown format (this_month)
    switch (behavior.toLowerCase()) {
      case 'fixed':
        return 'Fixed';
      case 'dynamic_today':
      case 'today':
        return 'Today';
      case 'dynamic_daily':
      case 'this_week':
        return 'This Week';
      case 'dynamic_weekly':
      case 'this_month':
        return 'This Month';
      case 'dynamic_monthly':
      case 'this_quarter':
        return 'This Quarter';
      case 'dynamic_quarterly':
      case 'this_year':
        return 'This Year';
      case 'dynamic_last_n_days':
      case 'last_n_days':
        // Extract the actual N value from chart data
        const lastNDays = this.getLastNDaysValue();
        return lastNDays ? `Last ${lastNDays} Days` : 'Last N Days';
      default:
        // For any other values, try to make them user-friendly
        return behavior.replace(/_/g, ' ').replace(/\b\w/g, l => l.toUpperCase());
    }
  }

  private getLastNDaysValue(): number | null {
    if (!this.chartData) {
      return null;
    }

    const chartDataAny = this.chartData as any;

    // Check multiple possible locations for last_n_days value
    // Prioritize chartData.Description.criteria_applied.last_n_days based on the provided JSON structure
    return chartDataAny.Description?.criteria_applied?.last_n_days ||
           chartDataAny.criteria?.last_n_days ||
           chartDataAny.config?.criteria?.last_n_days ||
           chartDataAny.last_n_days ||
           chartDataAny.lastNDays ||
           chartDataAny.config?.last_n_days ||
           (chartDataAny.config as any)?.duration_behavior?.last_n_days ||
           chartDataAny.data?.criteria?.last_n_days ||
           chartDataAny.data?.last_n_days ||
           null;
  }

  getDurationBehaviorClass(): string | null {
    const behavior = this.getDurationBehavior();
    if (!behavior) {
      return null;
    }

    // Return CSS class name for styling
    // Handle both existing API format (dynamic_monthly) and dropdown format (this_month)
    switch (behavior.toLowerCase()) {
      case 'fixed':
        return 'duration-badge-fixed';
      case 'dynamic_today':
      case 'today':
        return 'duration-badge-today';
      case 'dynamic_daily':
      case 'this_week':
        return 'duration-badge-this-week';
      case 'dynamic_weekly':
      case 'this_month':
        return 'duration-badge-this-month';
      case 'dynamic_monthly':
      case 'this_quarter':
        return 'duration-badge-this-quarter';
      case 'dynamic_quarterly':
      case 'this_year':
        return 'duration-badge-this-year';
      case 'dynamic_last_n_days':
      case 'last_n_days':
        return 'duration-badge-last-n-days';
      default:
        return 'duration-badge-default';
    }
  }

  // Table data methods
  hasTableData(): boolean {
    const tableData = this.getTableData();
    const hasData = tableData && Array.isArray(tableData) && tableData.length > 0;
    return hasData;
  }

  getTableData(): any[] {
    return extractChartTableRows(this.chartData);
  }

  getTableColumns(): string[] {
    return collectTableColumnKeys(this.getTableData());
  }

  formatColumnName(column: string): string {
    // Handle common column names with proper formatting
    const columnMap: { [key: string]: string } = {
      'category': 'Category',
      'name': 'Name',
      'dimension': 'Dimension',
      'secondary_dimension': 'Secondary Dimension',
      'time_bucket': 'Time Bucket',
      'count': 'Volume',
      'volume': 'Volume',
      'percentage': 'Percentage'
    };

    const mapped = columnMap[column.toLowerCase()];
    if (mapped) {
      return mapped;
    }

    // Convert snake_case to Title Case
    return column
      .split('_')
      .map(word => word.charAt(0).toUpperCase() + word.slice(1).toLowerCase())
      .join(' ');
  }

  formatCellValue(value: any, column?: string): string {
    if (value === null || value === undefined) {
      return '-';
    }
    if (typeof value === 'number') {
      // Check if this is a percentage column or value
      const isPercentageColumn = column && (column.toLowerCase() === 'percentage');
      const isPercentageValue = isPercentageColumn || (Math.abs(value) < 100 && value !== 0 && value.toString().includes('.'));

      if (isPercentageValue) {
        // Format percentage values with 2 decimal places
        return value.toFixed(2) + '%';
      } else {
        // Format regular numbers with commas for thousands (no spaces)
        return value.toString().replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      }
    }
    if (typeof value === 'string') {
      return value;
    }
    if (typeof value === 'boolean') {
      return value ? 'Yes' : 'No';
    }
    return String(value);
  }

  trackByIndex(index: number): number {
    return index;
  }

  rowInsightsValue(row: any): string | null {
    const described = describeRowDrilldown(row);
    if (!described) {
      return null;
    }
    if (described.kind === 'value') {
      return described.value;
    }
    return described.kind === 'period' ? described.slice.label : described.label;
  }

  selectedInsightsCount(): number {
    return this.selectedInsightRows().length;
  }

  allInsightRowsSelected(): boolean {
    const selectable = this.getTableData()
      .map((row, index) => ({ row, index }))
      .filter(({ row }) => !!this.rowInsightsValue(row));
    return selectable.length > 0 && selectable.every(({ index }) => this.selectedRowIndexes.has(index));
  }

  someInsightRowsSelected(): boolean {
    return this.selectedInsightsCount() > 0 && !this.allInsightRowsSelected();
  }

  isRowSelected(index: number): boolean {
    return this.selectedRowIndexes.has(index);
  }

  toggleRowSelection(index: number): void {
    if (this.insightsBusy) {
      return;
    }
    if (this.selectedRowIndexes.has(index)) {
      this.selectedRowIndexes.delete(index);
    } else {
      this.selectedRowIndexes.add(index);
    }
  }

  toggleAllInsightRows(): void {
    if (this.insightsBusy) {
      return;
    }
    const checked = !this.allInsightRowsSelected();
    this.selectedRowIndexes.clear();
    if (!checked) {
      return;
    }
    this.getTableData().forEach((row, index) => {
      if (this.rowInsightsValue(row)) {
        this.selectedRowIndexes.add(index);
      }
    });
  }

  openSelectedInsights(): void {
    this.openSelection('insights');
  }

  openSelectedNova(): void {
    this.openSelection('nova');
  }

  private openSelection(destination: 'insights' | 'nova'): void {
    const rows = this.selectedInsightRows()
      .map((row) => ({ row, described: describeRowDrilldown(row) }))
      .filter((item): item is { row: any; described: RowDrilldown } => !!item.described);
    if (!rows.length) {
      return;
    }

    const periodSelection = rows.every((item) => item.described.kind === 'period' || item.described.kind === 'empty-period');
    if (periodSelection) {
      const slices = rows
        .filter((item): item is { row: any; described: Extract<RowDrilldown, { kind: 'period' }> } => item.described.kind === 'period')
        .map((item) => item);
      if (!slices.length) {
        this.notificationService.info(this.destinationName(destination), 'The selected weeks fall outside those months.');
        return;
      }
      const merged = mergePeriodSlices(slices.map((item) => item.described.slice));
      this.confirmAndOpen(merged.dimensionValues, {
        dateRange: { startDate: merged.startDate, endDate: merged.endDate },
        skipDimensionFilter: merged.skipDimensionFilter,
        subjectLabel: merged.label,
        selections: slices.map((item) => this.selectionFromRow(
          item.row,
          item.described.slice.label,
          item.described.slice.startDate,
          item.described.slice.endDate
        )),
      }, destination);
      return;
    }

    const valueRows = rows.filter((item): item is { row: any; described: Extract<RowDrilldown, { kind: 'value' }> } => item.described.kind === 'value');
    const values = valueRows.map((item) => item.described.value);
    const secondaryValues = valueRows.map((item) => item.described.secondary);
    const defined = secondaryValues.filter((value): value is string => !!value);
    const uniqueSecondary = Array.from(new Set(defined.map((value) => value.toLowerCase())));
    const sharesOneSecondary = uniqueSecondary.length === 1 && defined.length === valueRows.length;
    this.confirmAndOpen(values, {
      secondaryValue: sharesOneSecondary ? defined[0] : null,
      secondarySkipped: uniqueSecondary.length > 1 || (defined.length > 0 && defined.length !== valueRows.length),
      selections: valueRows.map((item) => this.selectionFromRow(item.row, item.described.value)),
    }, destination);
  }

  private destinationName(destination: 'insights' | 'nova'): string {
    return destination === 'nova' ? 'NOVA' : 'Insights';
  }

  private confirmAndOpen(
    values: string[],
    options: {
      secondaryValue?: string | null;
      secondarySkipped?: boolean;
      dateRange?: { startDate: string; endDate: string } | null;
      skipDimensionFilter?: boolean;
      subjectLabel?: string;
      selections?: InsightsConfirmSelection[];
    },
    destination: 'insights' | 'nova'
  ): void {
    this.insightsBusy = true;
    this.chartInsightsDrilldown.loadContext(this.chartData).subscribe((context) => {
      this.insightsBusy = false;
      this.openInsightsConfirm(values, options, context, destination);
    });
  }

  private openInsightsConfirm(
    values: string[],
    options: {
      secondaryValue?: string | null;
      secondarySkipped?: boolean;
      dateRange?: { startDate: string; endDate: string } | null;
      skipDimensionFilter?: boolean;
      subjectLabel?: string;
      selections?: InsightsConfirmSelection[];
    },
    context: ReturnType<typeof readChartQueryContext>,
    destination: 'insights' | 'nova'
  ): void {
    const message = buildInsightsConfirmMessage({
      chartTitle: this.chartData?.title,
      context,
      values,
      subjectLabel: options.subjectLabel,
      dateRange: options.dateRange,
      skipDimensionFilter: options.skipDimensionFilter,
      secondaryValue: options.secondaryValue,
      secondarySkipped: options.secondarySkipped,
      selections: options.selections,
    });
    const openingNova = destination === 'nova';
    this.confirmPopupService.openConfirmDialog({
      title: openingNova
        ? 'We are opening NOVA with the following'
        : 'We are moving to the Insights Dashboard with the following',
      message: '',
      blocks: message,
      confirmText: 'Proceed',
      cancelText: 'Cancel',
      tone: 'neutral',
    }).subscribe((confirmed: boolean) => {
      if (confirmed) {
        this.launchSelection(values, options, destination);
      }
    });
  }

  private selectionFromRow(row: any, label: string, startDate?: string, endDate?: string): InsightsConfirmSelection {
    return {
      label,
      volume: chartTableVolumeLabel(row),
      startDate: startDate ?? null,
      endDate: endDate ?? null,
    };
  }

  private selectedInsightRows(): any[] {
    return this.getTableData().filter((row, index) => this.selectedRowIndexes.has(index) && !!this.rowInsightsValue(row));
  }

  private launchSelection(
    values: string[],
    options: {
      secondaryValue?: string | null;
      secondarySkipped?: boolean;
      dateRange?: { startDate: string; endDate: string } | null;
      skipDimensionFilter?: boolean;
      subjectLabel?: string;
    },
    destination: 'insights' | 'nova'
  ): void {
    this.insightsBusy = true;
    const request = destination === 'nova'
      ? this.chartInsightsDrilldown.openInNova(this.chartData, values, options)
      : this.chartInsightsDrilldown.open(this.chartData, values, options);
    request.subscribe({
      error: () => {
        this.insightsBusy = false;
      },
    });
  }

  // Calculate total row for table data
  getTotalRow(): any {
    const tableData = this.getTableData();
    if (!tableData || tableData.length === 0) {
      return null;
    }

    const columns = this.getTableColumns();
    const totalRow: any = {};

    columns.forEach(column => {
      if (column.toLowerCase() === 'category' || column.toLowerCase() === 'name' || column.toLowerCase() === 'dimension') {
        totalRow[column] = 'Total';
      } else if (column.toLowerCase() === 'secondary_dimension' || column.toLowerCase() === 'time_bucket') {
        totalRow[column] = '';
      } else if (column.toLowerCase() === 'percentage') {
        // For percentage columns, calculate sum of all percentage values
        const sum = tableData.reduce((acc: number, row: any) => {
          const value = row[column];
          if (typeof value === 'number' && !isNaN(value)) {
            return acc + value;
          }
          // Try to parse string numbers
          if (typeof value === 'string') {
            const parsedValue = parseFloat(value);
            if (!isNaN(parsedValue)) {
              return acc + parsedValue;
            }
          }
          return acc;
        }, 0);

        totalRow[column] = parseFloat(sum.toFixed(2));
      } else {
        // Sum all numeric values for this column
        const sum = tableData.reduce((acc: number, row: any) => {
          const value = row[column];
          if (typeof value === 'number' && !isNaN(value)) {
            return acc + value;
          }
          // Try to parse string numbers
          if (typeof value === 'string') {
            const parsedValue = parseFloat(value);
            if (!isNaN(parsedValue)) {
              return acc + parsedValue;
            }
          }
          return acc;
        }, 0);

        // Format the sum based on whether it's a whole number or decimal
        totalRow[column] = Number.isInteger(sum) ? sum : parseFloat(sum.toFixed(2));
      }
    });

    return totalRow;
  }
}
